import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { RenewalTaskFiltersComponent, RenewalTaskFilters } from './RenewalTaskFilters';
import { PaginationCustom } from './ui/pagination-custom';
import { getRenewalTasks, RenewalTask, RenewalTaskFilters as ApiFilters } from '../src/Utils/renewalTaskApi';
import { toast } from 'sonner';
import { Loader2, Eye, AlertCircle, CheckCircle, Clock, XCircle, AlertTriangle, ChevronRight, ChevronDown, ArrowLeft, Building2 } from 'lucide-react';
import { format } from 'date-fns';
import { TaskDetailsPanel } from './TaskDetailsPanel';
import { Sheet, SheetContent } from './ui/sheet';

interface RenewalTasksDashboardProps {
  viewMode?: 'my-tasks' | 'all-tasks';
  ownerUserId?: string;
}

export const RenewalTasksDashboard: React.FC<RenewalTasksDashboardProps> = ({
  viewMode = 'my-tasks',
  ownerUserId,
}) => {
  const { user, token } = useAuth();
  const [tasks, setTasks] = useState<RenewalTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [itemsPerPage] = useState(20);
  const [filters, setFilters] = useState<RenewalTaskFilters>({});
  const [selectedTask, setSelectedTask] = useState<RenewalTask | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set());
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metrics, setMetrics] = useState({
    overdue: 0,
    critical: 0,
    completedWeek: 0,
    slaOnTime: 0,
  });

  console.log('user:', tasks);
  // Determine ownerUserId - use prop if provided, otherwise use current user
  const effectiveOwnerUserId = ownerUserId || (viewMode === 'my-tasks' ? user?.id : undefined);

  // Fetch tasks
  useEffect(() => {
    const fetchTasks = async () => {
      if (!token) return;

      setLoading(true);
      try {
        // Map display status filter to backend status if needed
        // Note: VALID/EXPIRED/PENDING are display-only and need client-side filtering
        const apiFilters: ApiFilters = {
          ...filters,
          // If filter is VALID/EXPIRED/PENDING, remove it from API call and filter client-side
          status: filters.status && ['VALID', 'EXPIRED', 'PENDING'].includes(filters.status)
            ? undefined
            : filters.status as RenewalTask['status'] | undefined,
          // Use filters.ownerUserId if provided, otherwise use effectiveOwnerUserId
          ownerUserId: filters.ownerUserId || effectiveOwnerUserId,
          // Send clientSearch to API for server-side filtering (only if not empty)
          search: filters.clientSearch && filters.clientSearch.trim()
            ? filters.clientSearch.trim()
            : undefined,
          page: currentPage,
          limit: itemsPerPage,
        };

        const response = await getRenewalTasks(apiFilters, token);

        if (response.success) {
          let filteredTasks = response.data.tasks;

          // Filter by display status if needed (client-side only, as these are display concepts)
          if (filters.status && ['VALID', 'EXPIRED', 'PENDING'].includes(filters.status)) {
            filteredTasks = filteredTasks.filter((task: RenewalTask) => {
              const displayStatus = mapBackendToDisplayStatus(task.status, task.bucket, task.dueDate);
              return displayStatus === filters.status;
            });
          }

          console.log(' tasks:', filteredTasks);

          console.log('Filtered tasks:', response.data.tasks);
          setTasks(filteredTasks);
          setTotalPages(response.data.pagination.totalPages);
          setTotalCount(response.data.pagination.totalCount);
        } else {
          toast.error('Failed to load tasks');
        }
      } catch (error: any) {
        console.error('Error fetching tasks:', error);
        toast.error(error.message || 'Failed to load tasks');
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, [token, filters, currentPage, itemsPerPage, effectiveOwnerUserId]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Reset selected client when filters change
  useEffect(() => {
    setSelectedClientId(null);
    setSelectedTask(null); // Also clear selected task when filters change
  }, [filters]);

  // Extract staff members from tasks
  const staffMembers = useMemo(() => {
    const staffMap = new Map<string, { id: string; label: string }>();
    tasks.forEach((task) => {
      if (task.owner?.id) {
        if (!staffMap.has(task.owner.id)) {
          const label = task.owner.firstName && task.owner.lastName
            ? `${task.owner.firstName} ${task.owner.lastName}`
            : task.owner.email || task.owner.id;
          staffMap.set(task.owner.id, { id: task.owner.id, label });
        }
      }
    });
    return Array.from(staffMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [tasks]);

  // Extract clients from tasks
  const clients = useMemo(() => {
    const clientMap = new Map<string, { id: string; name: string }>();
    tasks.forEach((task) => {
      const clientId = task.clientId || task.clientName || '';
      if (clientId && !clientMap.has(clientId)) {
        clientMap.set(clientId, {
          id: clientId,
          name: task.clientName || clientId,
        });
      }
    });
    return Array.from(clientMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [tasks]);

  // Group tasks by client (tasks are already filtered by API)
  const groupedTasks = useMemo(() => {
    const groups = new Map<string, RenewalTask[]>();
    tasks.forEach((task) => {
      // Use clientId if available, otherwise use clientName, fallback to 'unknown'
      const clientId = task.clientId || task.clientName || 'unknown';
      if (!groups.has(clientId)) {
        groups.set(clientId, []);
      }
      groups.get(clientId)!.push(task);
    });
    return groups;
  }, [tasks]);

  // Get client summary stats
  const clientSummaries = useMemo(() => {
    return Array.from(groupedTasks.entries()).map(([clientId, clientTasks]) => {
      const firstTask = clientTasks[0];
      const overdueCount = clientTasks.filter(
        (t) => t.bucket === 'OVERDUE' || (t.dueDate && new Date(t.dueDate) < new Date())
      ).length;
      const criticalCount = clientTasks.filter((t) => t.priority === 'P0').length;
      const highPriorityCount = clientTasks.filter((t) => t.priority === 'P0' || t.priority === 'P1').length;

      return {
        clientId,
        clientName: firstTask.clientName,
        taskCount: clientTasks.length,
        overdueCount,
        criticalCount,
        highPriorityCount,
        tasks: clientTasks,
      };
    });
  }, [groupedTasks]);

  // Get tasks for selected client
  const selectedClientTasks = useMemo(() => {
    if (!selectedClientId) return [];
    return groupedTasks.get(selectedClientId) || [];
  }, [selectedClientId, groupedTasks]);

  const toggleClientExpansion = (clientId: string) => {
    const newExpanded = new Set(expandedClients);
    if (newExpanded.has(clientId)) {
      newExpanded.delete(clientId);
    } else {
      newExpanded.add(clientId);
    }
    setExpandedClients(newExpanded);
  };

  const handleClientSelect = (clientId: string) => {
    setSelectedClientId(clientId);
    setSelectedTask(null); // Clear selected task when switching to client view
  };

  const handleBackToAllClients = () => {
    setSelectedClientId(null);
    setSelectedTask(null); // Clear selected task when going back
  };

  const handleTaskClick = (task: RenewalTask, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setSelectedTask(task);
  };

  const handleClosePanel = () => {
    setSelectedTask(null);
  };

  useEffect(() => {
    if (!token) return;

    const fetchMetrics = async () => {
      setMetricsLoading(true);
      try {
        const [overdueRes, criticalRes, completedRes, slaRes] = await Promise.all([
          getRenewalTasks(
            { ownerUserId: effectiveOwnerUserId, bucket: 'OVERDUE', limit: 1, page: 1 },
            token
          ).catch(() => ({ success: false, data: { pagination: { totalCount: 0 } } })),
          getRenewalTasks(
            { ownerUserId: effectiveOwnerUserId, priority: 'P0', limit: 1, page: 1 },
            token
          ).catch(() => ({ success: false, data: { pagination: { totalCount: 0 } } })),
          getRenewalTasks(
            { ownerUserId: effectiveOwnerUserId, status: 'CLOSED', range: 'week', limit: 1, page: 1 },
            token
          ).catch(() => ({ success: false, data: { pagination: { totalCount: 0 } } })),
          getRenewalTasks(
            { ownerUserId: effectiveOwnerUserId, status: 'CLOSED', range: 'month', limit: 100, page: 1 },
            token
          ).catch(() => ({ success: false, data: { tasks: [], pagination: { totalCount: 0 } } })),
        ]);

        const closedTasks = slaRes?.data?.tasks || [];
        const slaEligible = closedTasks.filter((t) => t.dueDate);
        const slaOnTimeCount = slaEligible.filter(
          (t) => t.dueDate && new Date(t.updatedAt) <= new Date(t.dueDate)
        ).length;
        const slaOnTime =
          slaEligible.length > 0 ? Math.round((slaOnTimeCount / slaEligible.length) * 100) : 0;

        setMetrics({
          overdue: overdueRes?.data?.pagination?.totalCount || 0,
          critical: criticalRes?.data?.pagination?.totalCount || 0,
          completedWeek: completedRes?.data?.pagination?.totalCount || 0,
          slaOnTime,
        });
      } catch (error) {
        console.error('Failed to load task metrics', error);
      } finally {
        setMetricsLoading(false);
      }
    };

    fetchMetrics();
  }, [token, effectiveOwnerUserId]);

  const handleTaskUpdate = async () => {
    // Refresh tasks after update
    if (!token) return;

    try {
      const apiFilters: ApiFilters = {
        ...filters,
        ownerUserId: effectiveOwnerUserId,
        page: currentPage,
        limit: itemsPerPage,
      };

      const response = await getRenewalTasks(apiFilters, token);

      if (response.success) {
        setTasks(response.data.tasks);
        setTotalPages(response.data.pagination.totalPages);
        setTotalCount(response.data.pagination.totalCount);

        // Update selected task with fresh data if it exists
        if (selectedTask) {
          const updatedTask = response.data.tasks.find((t: RenewalTask) => t.id === selectedTask.id);
          if (updatedTask) {
            setSelectedTask(updatedTask);
          }
        }
      }
    } catch (error) {
      console.error('Error refreshing tasks:', error);
    }
  };

  // Map display status filter to backend status(es) for API
  const mapDisplayStatusToBackendFilter = (displayStatus: 'VALID' | 'EXPIRED' | 'PENDING'): RenewalTask['status'][] | undefined => {
    if (displayStatus === 'VALID') return ['CLOSED', 'RESOLVED'];
    if (displayStatus === 'EXPIRED') return ['OPEN']; // Will filter by bucket on client side
    if (displayStatus === 'PENDING') return ['OPEN', 'IN_PROGRESS', 'WAITING'];
    return undefined;
  };

  // Map backend status to display status (Valid/Expired/Pending)
  const mapBackendToDisplayStatus = (status: RenewalTask['status'], bucket?: RenewalTask['bucket'], dueDate?: string): 'VALID' | 'EXPIRED' | 'PENDING' => {
    if (status === 'CLOSED' || status === 'RESOLVED') return 'VALID';
    if (status === 'OPEN' && (bucket === 'OVERDUE' || (dueDate && new Date(dueDate) < new Date()))) return 'EXPIRED';
    return 'PENDING';
  };

  const getStatusBadge = (status: RenewalTask['status'], task?: RenewalTask) => {
    const displayStatus = mapBackendToDisplayStatus(status, task?.bucket, task?.dueDate);
    const variants: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
      VALID: { variant: 'default', label: 'Valid' },
      EXPIRED: { variant: 'destructive', label: 'Expired' },
      PENDING: { variant: 'outline', label: 'Pending' },
    };

    const config = variants[displayStatus] || variants.PENDING;
    if (!config) return null;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getPriorityBadge = (priority: RenewalTask['priority']) => {
    const variants: Record<string, { className: string; label: string; icon: React.ReactNode }> = {
      P0: { className: 'bg-red-100 text-red-800 border-red-300', label: 'P0', icon: <AlertCircle className="w-3 h-3" /> },
      P1: { className: 'bg-orange-100 text-orange-800 border-orange-300', label: 'P1', icon: <AlertTriangle className="w-3 h-3" /> },
      P2: { className: 'bg-yellow-100 text-yellow-800 border-yellow-300', label: 'P2', icon: <Clock className="w-3 h-3" /> },
      P3: { className: 'bg-blue-100 text-blue-800 border-blue-300', label: 'P3', icon: <CheckCircle className="w-3 h-3" /> },
      P4: { className: 'bg-gray-100 text-gray-800 border-gray-300', label: 'P4', icon: <CheckCircle className="w-3 h-3" /> },
    };

    const config = variants[priority] || variants.P4;
    if (!config) return null;
    return (
      <Badge variant="outline" className={`${config.className} flex items-center gap-1`}>
        {config.icon}
        {config.label}
      </Badge>
    );
  };

  const getBucketLabel = (bucket: RenewalTask['bucket']) => {
    const labels: Record<string, string> = {
      OVERDUE: 'Overdue',
      D0_3: 'Due 0-3 days',
      D4_7: 'Due 4-7 days',
      D8_14: 'Due 8-14 days',
      D15_30: 'Due 15-30 days',
      D31_90: 'Due 31-90 days',
      D90_PLUS: 'Due 90+ days',
    };
    return labels[bucket] || bucket;
  };

  // Normalize section names to match the table format
  const normalizeSectionName = (section: string): string => {
    const normalized = section.trim();
    // Map common variations to the correct format
    const sectionMap: Record<string, string> = {
      'Intake / Annual': 'Intake/ Annual',
      'Intake/Annual': 'Intake/ Annual',
      'Intake /Annual': 'Intake/ Annual',
      'Assessments': 'Assessments:',
      'DX / Eligibility': 'DX/ Eligiblity',
      'DX/Eligibility': 'DX/ Eligiblity',
      'DX /Eligibility': 'DX/ Eligiblity',
      'DX/Eligiblity': 'DX/ Eligiblity',
      'DX / Eligiblity': 'DX/ Eligiblity',
    };
    return sectionMap[normalized] || normalized;
  };

  const getTaskDates = (task: RenewalTask) => {

    if (task.startDate || task.endDate) {
      return { startDate: task.startDate, endDate: task.endDate };
    }

    const sections = task.source?.sections;
    if (!sections) return { startDate: undefined, endDate: undefined };

    if (sections['Plans'] && Array.isArray(sections['Plans'])) {

      const plans = sections['Plans'];

      // We need to compare dates to find the RIGHT plan. 
      // If we just pick the first or last one, we get the wrong dates (like Feb 2026).
      const targetDate = task.dueDate ? new Date(task.dueDate) : new Date();

      const matchedPlan = plans.find(plan => {
        // Skip plans with missing dates
        if (!plan.startDate || !plan.endDate) return false;

        // Parse "MM/DD/YYYY" manually to avoid timezone issues
        const startParts = plan.startDate.split('/');
        const endParts = plan.endDate.split('/');

        // Create simplified dates for comparison
        const start = new Date(`${startParts[2]}-${startParts[0]}-${startParts[1]}`);
        const end = new Date(`${endParts[2]}-${endParts[0]}-${endParts[1]}`);

        // Check if the Task Due Date falls inside this Plan's range
        // (Using a slight buffer of +/- days if needed, but strict for now)
        const isMatch = targetDate >= start && targetDate <= end;

        if (isMatch) {
          console.log(`   Possible match found: ${plan.taskName} (${plan.startDate} - ${plan.endDate})`);
        }
        return isMatch;
      });

      if (matchedPlan) {
        console.log('   >>> SELECTED PLAN:', matchedPlan.taskName);
        return { startDate: matchedPlan.startDate, endDate: matchedPlan.endDate };
      } else {
        console.log('   No plan found covering the due date. Checking for any valid plan...');
      }
    }

    // 3. Fallback: Standard Name Matching
    // (This runs for other sections like "Assessments" or "Intake")
    const normalizeForMatch = (name: string) =>
      name.toLowerCase().replace(/[^a-z0-9]/g, '');
    const normalizedTaskName = normalizeForMatch(task.itemName);

    for (const sectionName in sections) {
      if (sectionName === 'Plans') continue; // We already checked Plans above

      const items = sections[sectionName];
      if (!items || !Array.isArray(items)) continue;

      const match = items.find(item => {
        const itemTaskName = item.taskName || item.task || '';
        return normalizeForMatch(itemTaskName) === normalizedTaskName;
      });

      if (match) {
        console.log(`   >>> Match found in [${sectionName}]:`, match.taskName);
        return { startDate: match.startDate, endDate: match.endDate };
      }
    }

    console.log('   No matching dates found.');
    return { startDate: undefined, endDate: undefined };
  };

  const renderTasksTable = (tasksToRender: RenewalTask[]) => {
    // Group tasks by section (simple computation, not a hook)
    const groups = new Map<string, RenewalTask[]>();
    tasksToRender.forEach((task) => {
      const rawSection = task.sectionName || 'Other';
      const section = normalizeSectionName(rawSection);
      if (!groups.has(section)) {
        groups.set(section, []);
      }
      groups.get(section)!.push(task);
    });
    const groupedBySection = Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));

    return (
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[200px]">Task</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Bucket</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Start Date</TableHead>
              <TableHead>End Date</TableHead>
              <TableHead>Due Date</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groupedBySection.map(([section, sectionTasks]) => (
              <React.Fragment key={section}>
                {/* Section Header Row */}
                <TableRow className="bg-gray-50 hover:bg-gray-50">
                  <TableCell colSpan={8} className="font-semibold text-sm py-2">
                    {section}
                  </TableCell>
                </TableRow>
                {/* Tasks in this section */}
                {sectionTasks.map((task) => {
                  const { startDate, endDate } = getTaskDates(task);
                  return (
                    <TableRow
                      key={task.id}
                      className={`cursor-pointer hover:bg-gray-50 ${selectedTask?.id === task.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                        }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTaskClick(task, e);
                      }}
                    >
                      <TableCell className="font-medium">
                        <div>
                          <div className="font-semibold">{task.itemName}</div>
                          {task.itemCode && (
                            <div className="text-xs text-gray-500">{task.itemCode}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{getPriorityBadge(task.priority)}</TableCell>
                      <TableCell>
                        <span className="text-sm">{getBucketLabel(task.bucket)}</span>
                      </TableCell>
                      <TableCell>{getStatusBadge(task.status, task)}</TableCell>
                      <TableCell>
                        {startDate ? (
                          <span className="text-sm">
                            {format(new Date(startDate), 'MMM dd, yyyy')}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {endDate ? (
                          <span className="text-sm">
                            {format(new Date(endDate), 'MMM dd, yyyy')}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {task.dueDate ? (
                          <span className="text-sm">
                            {format(new Date(task.dueDate), 'MMM dd, yyyy')}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">No due date</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTaskClick(task);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Renewal Tasks</h1>
          <p className="text-sm text-gray-600 mt-1">
            {viewMode === 'my-tasks' ? 'Your assigned renewal tasks' : 'All renewal tasks'}
          </p>
        </div>
      </div>

      {/* Staff Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Overdue</p>
            <p className="text-2xl font-semibold mt-2">
              {metricsLoading ? '—' : metrics.overdue}
            </p>
            <p className="text-xs text-gray-500 mt-1">Tasks past due</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Critical (P0)</p>
            <p className="text-2xl font-semibold mt-2">
              {metricsLoading ? '—' : metrics.critical}
            </p>
            <p className="text-xs text-gray-500 mt-1">Highest priority tasks</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Completed (7d)</p>
            <p className="text-2xl font-semibold mt-2">
              {metricsLoading ? '—' : metrics.completedWeek}
            </p>
            <p className="text-xs text-gray-500 mt-1">Closed in last week</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">SLA On-Time</p>
            <p className="text-2xl font-semibold mt-2">
              {metricsLoading ? '—' : `${metrics.slaOnTime}%`}
            </p>
            <p className="text-xs text-gray-500 mt-1">Closed before due date (30d)</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters at Top */}
      <RenewalTaskFiltersComponent
        filters={filters}
        onFiltersChange={setFilters}
        staffMembers={staffMembers}
        clients={clients}
      />

      {/* Main Content */}
      {loading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            <span className="ml-2 text-sm text-gray-600">Loading tasks...</span>
          </CardContent>
        </Card>
      ) : tasks.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <XCircle className="w-12 h-12 text-gray-400 mb-4" />
            <p className="text-sm text-gray-600">No tasks found</p>
            <p className="text-xs text-gray-500 mt-1">
              Try adjusting your filters
            </p>
          </CardContent>
        </Card>
      ) : selectedClientId ? (
        // Split View: Client List (Left) + Client Tasks (Right)
        <div className="grid grid-cols-[300px_1fr] gap-4">
          {/* Left: Client List */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Clients</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
                {clientSummaries.map((summary) => (
                  <div
                    key={summary.clientId}
                    className={`p-3 border-b cursor-pointer transition-colors ${selectedClientId === summary.clientId
                      ? 'bg-blue-50 border-blue-200'
                      : 'hover:bg-gray-50'
                      }`}
                    onClick={() => handleClientSelect(summary.clientId)}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Building2 className="w-4 h-4 text-gray-400" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{summary.clientName}</div>
                        <div className="text-xs text-gray-500 truncate">{summary.clientId}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2 text-xs">
                      <Badge variant="outline" className="text-xs">
                        {summary.taskCount} tasks
                      </Badge>
                      {summary.overdueCount > 0 && (
                        <Badge variant="destructive" className="text-xs">
                          {summary.overdueCount} overdue
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Right: Client Tasks Table */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">
                    {clientSummaries.find((c) => c.clientId === selectedClientId)?.clientName || 'Client Tasks'}
                  </CardTitle>
                  <p className="text-sm text-gray-600 mt-1">
                    {(() => {
                      if (selectedClientTasks.length === 0) {
                        return '0 tasks';
                      }

                      // Check if all tasks belong to the same owner
                      const firstOwner = selectedClientTasks[0]?.owner;
                      const allSameOwner = selectedClientTasks.every(
                        (task) => task.owner?.id === firstOwner?.id
                      );

                      if (allSameOwner && firstOwner) {
                        const staffName = firstOwner.firstName && firstOwner.lastName
                          ? `${firstOwner.firstName} ${firstOwner.lastName}`
                          : firstOwner.email || 'Unknown';
                        return (
                          <>
                            Staff Assigned To: {staffName} • {selectedClientTasks.length} task{selectedClientTasks.length !== 1 ? 's' : ''}
                          </>
                        );
                      }

                      return `${selectedClientTasks.length} task${selectedClientTasks.length !== 1 ? 's' : ''}`;
                    })()}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBackToAllClients}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to All Clients
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {selectedClientTasks.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <XCircle className="w-12 h-12 text-gray-400 mb-4" />
                  <p className="text-sm text-gray-600">No tasks found for this client</p>
                </div>
              ) : (
                renderTasksTable(selectedClientTasks)
              )}
            </CardContent>
          </Card>

        </div>
      ) : (
        // Grouped View: All Clients with Expandable Rows
        <div>
          {/* Tasks Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                Tasks by Client ({totalCount})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]"></TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead className="text-center">Tasks</TableHead>
                      <TableHead className="text-center">Overdue</TableHead>
                      <TableHead className="text-center">Critical</TableHead>
                      <TableHead className="text-center">High Priority</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clientSummaries.map((summary) => {
                      const isExpanded = expandedClients.has(summary.clientId);
                      return (
                        <React.Fragment key={summary.clientId}>
                          <TableRow
                            className="cursor-pointer hover:bg-gray-50"
                            onClick={() => handleClientSelect(summary.clientId)}
                          >
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleClientExpansion(summary.clientId);
                                }}
                                className="h-6 w-6 p-0"
                              >
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4" />
                                ) : (
                                  <ChevronRight className="w-4 h-4" />
                                )}
                              </Button>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-gray-400" />
                                <div>
                                  <div className="font-medium">{summary.clientName}</div>
                                  <div className="text-xs text-gray-500">{summary.clientId}</div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline">{summary.taskCount}</Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              {summary.overdueCount > 0 ? (
                                <Badge variant="destructive">{summary.overdueCount}</Badge>
                              ) : (
                                <span className="text-gray-400">0</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {summary.criticalCount > 0 ? (
                                <Badge className="bg-red-100 text-red-800 border-red-300">
                                  {summary.criticalCount}
                                </Badge>
                              ) : (
                                <span className="text-gray-400">0</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {summary.highPriorityCount > 0 ? (
                                <Badge variant="outline">{summary.highPriorityCount}</Badge>
                              ) : (
                                <span className="text-gray-400">0</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleClientSelect(summary.clientId);
                                }}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                          {isExpanded && (
                            <TableRow>
                              <TableCell colSpan={7} className="p-0 bg-gray-50">
                                <div className="p-4" onClick={(e) => e.stopPropagation()}>
                                  {renderTasksTable(summary.tasks)}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="mt-4">
                  <PaginationCustom
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={totalCount}
                    itemsPerPage={itemsPerPage}
                    onPageChange={setCurrentPage}
                    onItemsPerPageChange={() => { }}
                    showPageSizeSelector={false}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Task Details Sheet - Slide-in drawer from right */}
      <Sheet open={!!selectedTask} onOpenChange={(open) => {
        if (!open) {
          handleClosePanel();
        }
      }}>
        <SheetContent side="right" className="w-full sm:max-w-[640px] overflow-y-auto p-0">
          {selectedTask && (
            <TaskDetailsPanel
              task={selectedTask}
              onUpdate={handleTaskUpdate}
              onClose={handleClosePanel}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

